import { Fragment, useMemo, useRef } from 'react';
import { DndContext } from '@dnd-kit/core';
import { formatDateKey, formatWeekLabel, getDayLabel, getISOWeekNumber, getProjectleiderColor, isToday, type FeestdagInfo } from '@/utils/planning';
import { splitPerDayIntoBands, type DayBand, type DayColumns } from '@/utils/planningPerDay';
import { usePlanningStore } from '@/stores/planning';
import { PlanningCard } from './PlanningCard';
import { PlanningZoomContext } from './PlanningZoom';
import { StickyScrollbar, chunkBoardWeeks, useFocusWeekScroll } from './MultiWeekBoard';
import type { OdooTask } from '@/types/odoo';

const NEUTRAL_BAND = { bg: 'bg-gray-100', border: 'border-l-gray-300', dot: 'bg-gray-400' };

/** Band colours: the PL's card colour (as on the planning cards); neutral grey for
 *  the two shared bands. */
function bandColors(band: DayBand): { bg: string; border: string; dot: string } {
  if (band.key === 'alle' || band.key === 'zonder') return NEUTRAL_BAND;
  const first = [...band.columns.values()].find((c) => c.length > 0)?.[0];
  return first ? getProjectleiderColor(first) : NEUTRAL_BAND;
}

interface PlanningDayBoardProps {
  columns: DayColumns;
  days: Date[];
  feestdagDates?: Map<string, FeestdagInfo>;
  onCardClick?: (task: OdooTask) => void;
  /** Multi-week: the selected week's Monday (see PlanningGrid). */
  focusWeekKey?: string;
}

/**
 * "Per dag" planning board: one column per day, and one band per projectleider
 * across those days ("Alle projectleiders" and "Zonder projectleider" at the
 * bottom), with each band's cards stacked per day and no installer rows. An overview only: no drag, no cell menu, no row reorder. A
 * card still opens its details (desktop) or its read-only action sheet (mobile).
 */
export function PlanningDayBoard({ columns, days, feestdagDates, onCardClick, focusWeekKey }: PlanningDayBoardProps) {
  const zoom = usePlanningStore((s) => s.zoom);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dateKeys = useMemo(() => days.map(formatDateKey), [days]);
  const todayKey = useMemo(() => {
    const d = days.find(isToday);
    return d ? formatDateKey(d) : null;
  }, [days]);
  const multiWeek = focusWeekKey != null;
  const weekStartKeys = useMemo(
    () => new Set(multiWeek ? days.filter((d, i) => i > 0 && d.getDay() === 1).map(formatDateKey) : []),
    [days, multiWeek],
  );
  useFocusWeekScroll(scrollRef, `day:${dateKeys[0] ?? ''}@${zoom}`, focusWeekKey ?? null, zoom, 0);

  const bands = useMemo(() => splitPerDayIntoBands(columns), [columns]);
  const weekStartCls = (dk: string) => (weekStartKeys.has(dk) ? ' border-l-[6px] border-l-gray-300' : '');

  return (
    <>
      <PlanningZoomContext.Provider value={zoom}>
      {/* No sensors are armed: cards render with drag disabled (overview mode). */}
      <DndContext>
      <div ref={scrollRef} className={`overflow-x-auto overscroll-x-contain ${multiWeek ? 'lg:mx-8 lg:[scrollbar-width:none]' : 'lg:px-8'} pb-24 lg:pb-8`}>
        <table
          className={`w-full ${multiWeek ? 'min-w-[4800px]' : 'min-w-[900px]'} border-collapse border border-gray-200 bg-white text-sm table-fixed`}
          style={zoom === 1 ? undefined : { zoom }}
          aria-label="Planning per dag"
        >
          <thead>
            {multiWeek && (
              <tr className="bg-gray-50 border-b border-gray-200">
                {chunkBoardWeeks(days).map((week, i) => (
                  <th
                    key={week.key}
                    colSpan={week.days.length}
                    className={`px-3 py-1.5 text-xs font-bold text-td-navy text-left ${i > 0 ? 'border-l-[6px] border-l-gray-300' : ''}`}
                  >
                    {formatWeekLabel(getISOWeekNumber(week.days[0]), week.days)}
                  </th>
                ))}
              </tr>
            )}
            <tr className="bg-gray-50 border-b border-gray-200">
              {days.map((day) => {
                const dk = formatDateKey(day);
                const feestdag = feestdagDates?.get(dk);
                return (
                    <th
                      key={dk}
                      data-date-key={dk}
                      className={`px-2 py-2 text-xs font-bold text-center border-r border-gray-200 last:border-r-0${weekStartCls(dk)} ${
                        feestdag ? 'bg-purple-100 text-purple-700'
                        : dk === todayKey ? 'bg-td-blue/10 text-td-blue'
                        : 'text-td-navy'
                      }`}
                    >
                      {getDayLabel(day)}
                      {feestdag && (
                        <span className="block text-[10px] font-semibold text-purple-500 mt-0.5">{feestdag.name}</span>
                      )}
                    </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {bands.length > 0 ? (
              bands.map((band) => {
                const color = bandColors(band);
                return (
                <Fragment key={band.key}>
                  <tr className={`${color.bg} border-y border-gray-200`} data-testid={`band-header-${band.key}`}>
                    <td colSpan={days.length} className={`px-3 py-1.5 border-l-4 ${color.border}`}>
                      {/* Multi-week: the row is thousands of px wide; pin the label left. */}
                      <div className={`${multiWeek ? 'sticky left-3 inline-flex' : 'flex'} items-center gap-2`}>
                        <span className={`w-3 h-3 rounded-full shrink-0 ${color.dot}`} />
                        <span className="font-bold text-td-navy">{band.label}</span>
                      </div>
                    </td>
                  </tr>
                  <tr data-testid={`band-${band.key}`}>
                    {dateKeys.map((dk, i) => (
                      <td
                        key={dk}
                        data-testid={`dag-${dk}`}
                        className={`p-1 pb-3 align-top border-r border-gray-200 last:border-r-0${i === 0 ? ` border-l-4 ${color.border}` : weekStartCls(dk)} ${
                          feestdagDates?.has(dk) ? 'bg-purple-50'
                          : dk === todayKey ? 'bg-td-blue/5'
                          : ''
                        }`}
                      >
                        {(band.columns.get(dk) ?? []).map((task) => (
                          <PlanningCard
                            key={task.id}
                            task={task}
                            // A tour card is per installer: its modal names that installer.
                            installerId={task.x_studio_installateur?.[0] ?? 0}
                            overview
                            onCardClick={onCardClick}
                            colorOverride={getProjectleiderColor(task)}
                          />
                        ))}
                      </td>
                    ))}
                  </tr>
                </Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={days.length} className="py-12 text-center text-td-muted">
                  {multiWeek ? 'Geen taken voor deze weken' : 'Geen taken voor deze week'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {multiWeek && <StickyScrollbar targetRef={scrollRef} />}
      </DndContext>
      </PlanningZoomContext.Provider>
    </>
  );
}
